// Pitch Match note choice (issue #3518): which notes one session asks the singer for. Pure
// functions and plain data shared by the exercise state machine, the page and the stories. No DOM,
// no audio. Tunables (the per-level tolerance, hold and jump, notes per session, the range trim)
// live in learn_rules.ts.
//
// The rules: every target is a natural note - the C major notes the Range Finder steps by, so no
// accidental appears before the singer is ready for one (issue #3338, story 38) - and sits inside
// the saved range trimmed by rangeTrimSemitones at each end, so nothing is asked for at the very
// edge of the voice. The first target is anywhere in those bounds; each later one is drawn
// uniformly from the natural notes within the level's maxJumpSemitones of the previous target and
// never equal to it, so jumps stay singable (a fourth on Easy) and every note is a new target.
//
// Randomness comes from the same RandomSource the Rhythm patterns use, so tests and stories seed
// it and get the same session every time.

import { defaultVocalRange, pitchLevelParams, pitchNotesPerSession, rangeTrimSemitones } from './learn_rules.ts';
import { LearnLevel, VocalRange } from './model.ts';
import { RandomSource } from './rhythm_echo.ts';

// The naturals: C major, the scale the Range Finder steps by, so both exercises ask for the same
// notes. The Range Finder's step (cMajorStep) stays in the client beside the ladder it draws, but
// it reads this one check (issue #3518).
const naturalPitchClasses = [0, 2, 4, 5, 7, 9, 11];

export function isNatural(midi: number): boolean {
  return naturalPitchClasses.includes(((midi % 12) + 12) % 12);
}

// Every natural midi from lowMidi to highMidi inclusive, ascending; bounds that are not naturals
// themselves are simply not in the list.
export function naturalMidisBetween(lowMidi: number, highMidi: number): number[] {
  const midis: number[] = [];
  for (let midi = Math.ceil(lowMidi); midi <= Math.floor(highMidi); midi += 1) {
    if (!isNatural(midi)) {
      continue;
    }
    midis.push(midi);
  }
  return midis;
}

// The band the session's targets are drawn from: the measured range trimmed at each end. A session
// must always be buildable, and two natural notes are the fewest that let consecutive targets
// differ, so a range too narrow to trim is used as measured, and one too narrow even untrimmed (a
// Range Finder run that matched almost nothing) falls back to the default range.
export function pitchTargetBounds(range: VocalRange): VocalRange {
  const trimmed: VocalRange = {
    lowMidi: range.lowMidi + rangeTrimSemitones,
    highMidi: range.highMidi - rangeTrimSemitones,
  };
  if (naturalMidisBetween(trimmed.lowMidi, trimmed.highMidi).length >= 2) {
    return trimmed;
  }
  if (naturalMidisBetween(range.lowMidi, range.highMidi).length >= 2) {
    return range;
  }
  return defaultVocalRange;
}

function pick(candidates: number[], random: RandomSource): number {
  return candidates[Math.min(candidates.length - 1, Math.floor(random() * candidates.length))];
}

// The session's targets, in order. Consecutive naturals are one or two semitones apart and every
// level jumps at least a fourth, so there is always a candidate for the next target.
export function pickPitchTargets(
  range: VocalRange,
  level: LearnLevel,
  random: RandomSource = Math.random,
  count = pitchNotesPerSession,
): number[] {
  const bounds = pitchTargetBounds(range);
  const naturals = naturalMidisBetween(bounds.lowMidi, bounds.highMidi);
  const { maxJumpSemitones } = pitchLevelParams[level];
  const targets: number[] = [];
  for (let index = 0; index < count; index += 1) {
    const previous = targets[targets.length - 1];
    const candidates =
      previous === undefined
        ? naturals
        : naturals.filter((midi) => midi !== previous && Math.abs(midi - previous) <= maxJumpSemitones);
    targets.push(pick(candidates, random));
  }
  return targets;
}
